import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { AuthContext } from "src/context/AuthContext";
import { autoAppyUrl } from "src/config/autoAppyUrls";
import { CRM_HOME } from "src/config/crmRoutes";

export default function AutoAppyLogin() {
  const { loading, beginLogin } = useContext(AuthContext);
  const location = useLocation();
  const callbackError = new URLSearchParams(location.search).get("reason");
  const started = useRef(false);
  const [loginError, setLoginError] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const error = loginError || callbackError;

  const connect = useCallback(async () => {
    setRetrying(true);
    setLoginError(null);
    try {
      // App entry must use the current central browser identity, even when
      // Sales still has a valid session belonging to a previous account.
      await beginLogin(CRM_HOME);
    } catch {
      setLoginError("connection_failed");
      setRetrying(false);
    }
  }, [beginLogin]);

  useEffect(() => {
    if (!loading && !callbackError && !started.current) {
      started.current = true;
      connect();
    }
  }, [callbackError, connect, loading]);

  return (
    <div className="grid min-h-screen place-items-center bg-gradient-to-br from-sky-50 via-white to-teal-50 px-6 py-12 dark:from-[#0B1621] dark:via-[#102434] dark:to-[#12323a]">
      <section
        className="w-full max-w-lg rounded-3xl border border-sky-100 bg-white p-8 shadow-sm dark:border-slate-700 dark:bg-[#132736]"
        aria-labelledby="login-title"
      >
        <p className="mb-3 text-xs font-bold uppercase tracking-widest text-sky-700 dark:text-sky-300">
          Auto Appy Sales
        </p>
        <h1
          id="login-title"
          className="text-2xl font-bold text-[#123247] dark:text-white"
        >
          {error ? "Let’s get you into Sales" : "Opening your Sales workspace"}
        </h1>
        {error ? (
          <div role="alert">
            <p className="mt-4 leading-relaxed text-slate-600 dark:text-slate-300">
              {error === "access_denied"
                ? "We couldn’t confirm Sales access for the signed-in Auto Appy account. Check that you’re using the account invited to your organisation and that Sales access is enabled."
                : error === "state_mismatch"
                  ? "This sign-in attempt expired or could not be verified. Start a fresh sign-in to continue to Sales."
                  : "Your sign-in could not be completed. Try connecting again with your Auto Appy account."}
            </p>
            <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">
              Your organisation and team access are managed in Auto Appy.
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-4">
              <button
                type="button"
                onClick={connect}
                disabled={retrying}
                className="rounded-xl bg-sky-700 px-5 py-3 text-sm font-semibold text-white transition hover:bg-sky-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-600 disabled:opacity-60"
              >
                {retrying ? "Connecting…" : "Try again"}
              </button>
              <a
                className="text-sm font-semibold text-sky-700 underline underline-offset-4 dark:text-sky-300"
                href={autoAppyUrl("/")}
              >
                Return to Auto Appy
              </a>
            </div>
          </div>
        ) : (
          <p role="status" className="mt-4 text-slate-600 dark:text-slate-300">
            Connecting securely with your current Auto Appy account…
          </p>
        )}
      </section>
    </div>
  );
}
